/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
// @ts-nocheck

import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import {
  formatVideoCaption,
  formatVideoDuration,
} from '@carbon/ibmdotcom-utilities/es/utilities/formatVideoCaption/formatVideoCaption.js';
import C4DButtonGroupItem from '../button-group/button-group-item';
import CTAMixin from '../../component-mixins/cta/cta-v1';
import VideoCTAMixin from '../../component-mixins/cta/video';
import { CTA_TYPE } from './defs';
import styles from './cta.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

export { CTA_TYPE };

const { stablePrefix: c4dPrefix } = settings;

/**
 * Button CTA.
 *
 * @element c4d-button-cta
 */
@customElement(`${c4dPrefix}-button-cta`)
class C4DButtonCTA extends VideoCTAMixin(CTAMixin(C4DButtonGroupItem)) {
  /**
   * The button that may work as a link.
   *
   * @private
   */
  @query('[part="button"]')
  _linkNode!: HTMLElement;

  /**
   * @returns The main content.
   */
  protected _renderContent() {
    const { ctaType, _hasMainContent: hasMainContent } = this;
    if (ctaType !== CTA_TYPE.VIDEO) {
      return html` <slot @slotchange="${this._handleSlotChange}"></slot> `;
    }
    const {
      videoDuration,
      videoName,
      formatVideoCaption: formatVideoCaptionInEffect,
      formatVideoDuration: formatVideoDurationInEffect,
    } = this;
    const caption = hasMainContent
      ? undefined
      : formatVideoCaptionInEffect({
          duration: formatVideoDurationInEffect({
            duration: !videoDuration ? videoDuration : videoDuration * 1000,
          }),
          name: videoName,
        });
    return html`
      <slot @slotchange="${this._handleSlotChange}"></slot>${caption}
    `;
  }

  protected _renderInner() {
    return html` ${this._renderContent()}${this._renderIcon()} `;
  }

  /**
   * The CTA type.
   */
  @property({ reflect: true, attribute: 'cta-type' })
  ctaType = CTA_TYPE.REGULAR;

  /**
   * Link `href`.
   */
  @property({ attribute: 'href' })
  href?: string;

  /**
   * The formatter for the video caption, composed with the video name and the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  @property({ attribute: false })
  formatVideoCaption = formatVideoCaption;

  /**
   * The formatter for the video duration.
   * Should be changed upon the locale the UI is rendered with.
   */
  @property({ attribute: false })
  formatVideoDuration = formatVideoDuration;

  /**
   * The video duration.
   */
  @property({ type: Number, attribute: 'video-duration' })
  videoDuration?: number;

  /**
   * The video name.
   */
  @property({ attribute: 'video-name' })
  videoName?: string;

  /**
   * The video description.
   */
  @property({ attribute: 'video-description' })
  videoDescription?: string;

  /**
   * The video thumbnail URL.
   * Button CTA does not support video thumbnail, and this property should never be set.
   */
  videoThumbnailUrl?: never;

  static get stableSelector() {
    return `${c4dPrefix}--button-cta`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

console.warn(
  `The c4d-buton-cta component has been deprecated. All its features have been absorbed into
  the base c4d-button component. See migration guide for more information.`
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DButtonCTA;
